const initializedSections = new WeakSet();
const ACCORDION_TRANSITION_DURATION = 760;

const getSections = (scope = document) => {
  if (!scope) {
    return [];
  }

  if (scope.matches?.('.sectors-sec')) {
    return [scope];
  }

  return Array.from(scope.querySelectorAll?.('.sectors-sec') || []);
};

const getSectionItems = (section) => Array.from(section.querySelectorAll('[data-sectors-item]'))
  .map((item) => ({
    item,
    layout: item.querySelector('.sectors-sec__item-layout'),
    main: item.querySelector('.sectors-sec__item-main'),
    number: item.querySelector('.sectors-sec__item-number'),
    title: item.querySelector('.sectors-sec__item-title'),
    link: item.querySelector('.sectors-sec__item-link'),
    image: item.querySelector('.sectors-sec__item-image'),
    cta: item.querySelector('.sectors-sec__item-cta')
      || item.querySelector('.sectors-sec__item-layout .sectors-sec__item-link'),
    trigger: item.querySelector('[data-sectors-trigger]'),
    panel: item.querySelector('[data-sectors-panel]'),
    inner: item.querySelector('[data-sectors-panel-inner]'),
  }))
  .filter(({ layout, main, number, title, link, trigger }) => (
    layout && main && number && title && (link || trigger)
  ));

const getPixelValue = (element, propertyName, fallback = 0) => {
  if (!element) {
    return fallback;
  }

  const value = Number.parseFloat(window.getComputedStyle(element).getPropertyValue(propertyName));

  return Number.isFinite(value) ? value : fallback;
};

const getOuterHeight = (element) => {
  if (!element || window.getComputedStyle(element).display === 'none') {
    return 0;
  }

  return element.getBoundingClientRect().height
    + getPixelValue(element, 'margin-top')
    + getPixelValue(element, 'margin-bottom');
};

const getItemClosedHeight = ({ item, layout, number, title }) => {
  const minimumHeight = getPixelValue(item, '--sectors-closed-height', 140);

  if (getPixelValue(item, '--sectors-stacked-layout') !== 1) {
    return minimumHeight;
  }

  const verticalPadding = getPixelValue(layout, 'padding-top') + getPixelValue(layout, 'padding-bottom');
  const headerHeight = Math.max(getOuterHeight(number), getOuterHeight(title));
  const borders = getPixelValue(item, 'border-top-width') + getPixelValue(item, 'border-bottom-width');

  return Math.ceil(Math.max(minimumHeight, verticalPadding + headerHeight + borders));
};

const getItemActiveHeight = ({ item, layout, main, number, title, inner, panel, image, cta }) => {
  const verticalPadding = getPixelValue(layout, 'padding-top') + getPixelValue(layout, 'padding-bottom');
  const titleHeight = getOuterHeight(title);
  const minContentHeight = getPixelValue(item, '--sectors-open-media-height', 197);
  const linkHeight = getPixelValue(item, '--sectors-open-link-height');
  const isStackedLayout = getPixelValue(item, '--sectors-stacked-layout') === 1;
  const isImageFirstLayout = getPixelValue(item, '--sectors-image-first-layout') === 1;
  const linkGap = isStackedLayout ? getPixelValue(item, '--sectors-active-link-gap') : 0;
  const headerHeight = isImageFirstLayout ? Math.max(number.offsetHeight, titleHeight) : 0;
  const panelHeight = inner?.scrollHeight || 0;

  if (isStackedLayout && item.classList.contains('is-active')) {
    const imageHeight = getOuterHeight(image);
    const headerHeight = Math.max(getOuterHeight(number), getOuterHeight(title));
    const contentHeight = isImageFirstLayout ? imageHeight + headerHeight : Math.max(imageHeight, headerHeight);
    const ctaHeight = cta ? Math.max(linkHeight, cta.scrollHeight) : 0;
    const margins = getPixelValue(panel, 'margin-top') + getPixelValue(panel, 'margin-bottom')
      + getPixelValue(cta, 'margin-top') + getPixelValue(cta, 'margin-bottom');
    const rows = 1 + Number(isImageFirstLayout && imageHeight > 0) + Number(Boolean(panel)) + Number(Boolean(cta));
    const rowGaps = getPixelValue(layout, 'row-gap') * Math.max(0, rows - 1);
    const borders = getPixelValue(item, 'border-top-width') + getPixelValue(item, 'border-bottom-width');

    return Math.ceil(verticalPadding + contentHeight + panelHeight + ctaHeight + margins + rowGaps + borders);
  }

  const mainGap = getPixelValue(main, 'row-gap');
  const contentHeight = isImageFirstLayout
      ? minContentHeight + headerHeight + panelHeight
    : isStackedLayout
      ? Math.max(minContentHeight, titleHeight) + panelHeight
    : Math.max(minContentHeight, titleHeight + panelHeight + mainGap);

  return Math.ceil(verticalPadding + contentHeight + linkGap + linkHeight);
};

const updateItemMeasurements = (items) => {
  items.forEach(({ item, panel, inner, ...metrics }) => {
    if (panel && inner) {
      const panelHeight = `${inner.scrollHeight}px`;
      if (panel.style.getPropertyValue('--sectors-panel-height') !== panelHeight) {
        panel.style.setProperty('--sectors-panel-height', panelHeight);
      }
    }

    const heights = {
      '--sectors-closed-item-height': `${getItemClosedHeight({ item, ...metrics })}px`,
      '--sectors-active-height': `${getItemActiveHeight({ item, panel, inner, ...metrics })}px`,
    };
    Object.entries(heights).forEach(([property, value]) => {
      if (item.style.getPropertyValue(property) !== value) {
        item.style.setProperty(property, value);
      }
    });
  });
};

const setActiveItem = (items, activeItem) => {
  items.forEach(({ item, trigger, panel }) => {
    const isActive = item === activeItem;

    item.classList.toggle('is-active', isActive);
    trigger?.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    panel?.setAttribute('aria-hidden', isActive ? 'false' : 'true');
  });
};

const lockAccordionHeight = (accordion) => {
  const height = Math.ceil(accordion.getBoundingClientRect().height);

  accordion.style.minHeight = `${height}px`;

  return height;
};

const getAccordionTargetHeight = (accordion, items, activeItem) => {
  const accordionBorders = getPixelValue(accordion, 'border-top-width')
    + getPixelValue(accordion, 'border-bottom-width');
  const itemsHeight = items.reduce((total, { item }) => {
    const itemHeight = item === activeItem
      ? getPixelValue(item, '--sectors-active-height', 237)
      : getPixelValue(item, '--sectors-closed-item-height', getPixelValue(item, '--sectors-closed-height', 140));

    return total + itemHeight;
  }, 0);

  return Math.ceil(accordionBorders + itemsHeight);
};

const releaseAccordionHeight = (accordion, token, state) => {
  let released = false;

  const release = () => {
    if (released) {
      return;
    }

    released = true;
    accordion.removeEventListener('transitionend', onTransitionEnd);
    window.clearTimeout(releaseTimeout);

    if (token !== state.transitionToken) {
      return;
    }

    accordion.style.removeProperty('min-height');
  };

  const onTransitionEnd = (event) => {
    if (event.target === accordion && event.propertyName === 'min-height') {
      release();
    }
  };

  const releaseTimeout = window.setTimeout(release, ACCORDION_TRANSITION_DURATION + 120);

  accordion.addEventListener('transitionend', onTransitionEnd);
};

const initSectorsSection = (scope = document) => {
  getSections(scope).forEach((section) => {
    if (initializedSections.has(section)) {
      return;
    }

    const items = getSectionItems(section);

    if (!items.length) {
      return;
    }

    const accordion = section.querySelector('.sectors-sec__accordion');

    if (!accordion) {
      return;
    }

    initializedSections.add(section);

    const state = {
      currentActiveItem: items.find(({ item, trigger }) => (
        item.classList.contains('is-active') || trigger?.getAttribute('aria-expanded') === 'true'
      ))?.item || items[0].item,
      transitionToken: 0,
      updateFrame: null,
    };
    const hoverMedia = window.matchMedia?.('(hover: hover) and (pointer: fine)') || { matches: false };
    const reducedMotionMedia = window.matchMedia?.('(prefers-reduced-motion: reduce)') || { matches: false };

    const updateMeasurements = () => {
      updateItemMeasurements(items);
    };

    const requestMeasurementUpdate = () => {
      if (state.updateFrame) {
        return;
      }

      state.updateFrame = window.requestAnimationFrame(() => {
        state.updateFrame = null;
        updateMeasurements();

        if (accordion.style.minHeight) {
          accordion.style.minHeight = `${getAccordionTargetHeight(accordion, items, state.currentActiveItem)}px`;
        }
      });
    };

    const activateItem = (nextItem) => {
      if (!nextItem || nextItem === state.currentActiveItem) {
        return;
      }

      state.transitionToken += 1;
      const token = state.transitionToken;
      lockAccordionHeight(accordion);
      state.currentActiveItem = nextItem;
      setActiveItem(items, state.currentActiveItem);
      updateMeasurements();

      if (reducedMotionMedia.matches) {
        accordion.style.removeProperty('min-height');

        return;
      }

      const targetHeight = getAccordionTargetHeight(accordion, items, state.currentActiveItem);

      void accordion.offsetHeight;

      window.requestAnimationFrame(() => {
        if (token !== state.transitionToken) {
          return;
        }

        accordion.style.minHeight = `${targetHeight}px`;
        releaseAccordionHeight(accordion, token, state);
      });
    };

    setActiveItem(items, state.currentActiveItem);
    updateMeasurements();
    section.classList.add('is-ready');

    section.addEventListener('click', (event) => {
      const trigger = event.target.closest?.('[data-sectors-trigger]');

      if (!trigger || !section.contains(trigger)) {
        return;
      }

      activateItem(trigger.closest('[data-sectors-item]'));
    });

    section.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      const trigger = event.target.closest?.('[data-sectors-trigger]');

      if (!trigger || !section.contains(trigger)) {
        return;
      }

      event.preventDefault();
      activateItem(trigger.closest('[data-sectors-item]'));
    });

    items.forEach(({ item }) => {
      item.addEventListener('pointerenter', () => {
        if (hoverMedia.matches) {
          activateItem(item);
        }
      });
    });

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(requestMeasurementUpdate);
      items.forEach(({ inner, title, image }) => {
        [inner, title, image].filter(Boolean).forEach(element => resizeObserver.observe(element));
      });
    }

    items.forEach(({ image, cta }) => {
      const img = image?.querySelector('img');
      img?.addEventListener('load', requestMeasurementUpdate);
      img?.addEventListener('error', requestMeasurementUpdate);
      cta?.addEventListener('transitionend', (event) => {
        if (event.target === cta && ['height', 'padding-top', 'padding-bottom'].includes(event.propertyName)) {
          requestMeasurementUpdate();
        }
      });
    });

    document.fonts?.ready.then(requestMeasurementUpdate);
    document.fonts?.addEventListener('loadingdone', requestMeasurementUpdate);
    window.addEventListener('resize', requestMeasurementUpdate, { passive: true });
  });
};

initSectorsSection();
window.initSectorsSection = initSectorsSection;
